import React, { useState, useEffect } from 'react';
import { YachtItem } from '../../types';
import { useSite } from '../../context/SiteContext';
import { dbService } from '../../services/firebase';
import { STOCK_IMAGES } from '../../config/constants';
import {
  X,
  Anchor,
  Check,
  Image as ImageIcon,
  DollarSign,
  MapPin,
  Sparkles,
} from 'lucide-react';

interface YachtFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  yacht: YachtItem | null;
  onSave: () => void;
}

export const YachtFormModal: React.FC<YachtFormModalProps> = ({
  isOpen,
  onClose,
  yacht,
  onSave,
}) => {
  const { activeSite } = useSite();

  const getDefaultFormData = (): Partial<YachtItem> => ({
    name: '',
    slug: '',
    type: 'Motor Yacht',
    pricePerDay: 1500,
    currency: 'EUR',
    capacity: 6,
    cabins: 3,
    bathrooms: 3,
    lengthMeters: 20,
    yearBuilt: 2022,
    cruisingSpeedKnots: 16,
    portLocation: '',
    status: 'AVAILABLE',
    coverImage: STOCK_IMAGES[0]?.url || '',
    galleryImages: [],
    features: ['Wi-Fi', 'Klima', 'Jeneratör', 'Ses Sistemi'],
    description: '',
  });

  const [formData, setFormData] = useState<Partial<YachtItem>>(yacht ? { ...yacht } : getDefaultFormData());

  useEffect(() => {
    if (yacht) {
      setFormData({
        ...yacht,
        type: yacht.type || 'Motor Yacht',
        currency: yacht.currency || 'EUR',
        name: yacht.name || '',
        portLocation: yacht.portLocation || yacht.basePort || '',
        description: yacht.description || '',
        coverImage: yacht.coverImage || STOCK_IMAGES[0]?.url || '',
      });
    } else {
      setFormData(getDefaultFormData());
    }
  }, [yacht, isOpen]);

  const [newFeature, setNewFeature] = useState('');

  if (!isOpen || !activeSite) return null;

  const handleFeatureAdd = () => {
    if (!newFeature) return;
    const currentFeatures = formData.features || [];
    setFormData({ ...formData, features: [...currentFeatures, newFeature] });
    setNewFeature('');
  };

  const handleFeatureRemove = (index: number) => {
    const currentFeatures = (formData.features || []).filter((_, i) => i !== index);
    setFormData({ ...formData, features: currentFeatures });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) return;

    const slug = formData.slug || (formData.name || '').toLowerCase().replace(/[^a-z0-9]/g, '-');
    const yachtData: YachtItem = {
      id: yacht ? yacht.id : `yacht_${Date.now().toString(36)}`,
      siteId: activeSite.id,
      name: formData.name || '',
      slug,
      type: formData.type || 'Motor Yacht',
      pricePerDay: Number(formData.pricePerDay) || 1000,
      currency: formData.currency || 'EUR',
      capacity: Number(formData.capacity) || 6,
      cabins: Number(formData.cabins) || 3,
      lengthMeters: Number(formData.lengthMeters) || 20,
      buildYear: Number((formData as any).buildYear || (formData as any).yearBuilt) || 2020,
      cruisingSpeedKnots: Number(formData.cruisingSpeedKnots) || 15,
      portLocation: formData.portLocation || 'Bodrum',
      status: (formData.status as any) || 'AVAILABLE',
      coverImage: formData.coverImage || STOCK_IMAGES[0].url,
      features: formData.features || [],
      description: formData.description || '',
    };

    await dbService.saveYacht(activeSite.id, yachtData);
    onSave();
  };

  const yachtStockImages = STOCK_IMAGES;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-in fade-in select-none">
      <div className="bg-slate-900 border border-slate-800 w-full max-w-3xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-4 border-b border-slate-800 flex items-center justify-between text-white">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-cyan-500/20 text-cyan-400 flex items-center justify-center">
              <Anchor className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-base font-bold">
                {yacht ? 'Yat Bilgilerini Düzenle' : 'Filoya Yeni Yat Ekle'}
              </h3>
              <p className="text-xs text-slate-400">Yatın teknik özelliklerini, fiyatını ve fotoğraflarını belirleyin.</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-5 text-slate-200 text-xs">
          {/* Row 1: Name & Type */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block font-semibold mb-1 text-slate-300">Yat Adı *</label>
              <input
                required
                type="text"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Örn: Azure Princess 85ft"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 outline-none"
              />
            </div>
            <div>
              <label className="block font-semibold mb-1 text-slate-300">Yat Türü *</label>
              <select
                value={formData.type || 'Motor Yacht'}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 outline-none cursor-pointer"
              >
                <option value="Superyacht">Superyacht (Lüks Süperyat)</option>
                <option value="Motor Yacht">Motor Yacht (Motoryat)</option>
                <option value="Gulet">Gulet (Geleneksel Ahşap)</option>
                <option value="Catamaran">Catamaran (Katamaran)</option>
                <option value="Sailing Yacht">Sailing Yacht (Yelkenli)</option>
              </select>
            </div>
          </div>

          {/* Row 2: Price, Currency, Port */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block font-semibold mb-1 text-slate-300">Günlük Fiyat *</label>
              <input
                required
                type="number"
                min="0"
                value={formData.pricePerDay || ''}
                onChange={(e) => setFormData({ ...formData, pricePerDay: Number(e.target.value) })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 outline-none"
              />
            </div>
            <div>
              <label className="block font-semibold mb-1 text-slate-300">Para Birimi *</label>
              <select
                value={formData.currency || 'EUR'}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value as 'EUR' | 'USD' | 'TRY' })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 outline-none"
              >
                <option value="EUR">EUR (€)</option>
                <option value="USD">USD ($)</option>
                <option value="TRY">TRY (₺)</option>
              </select>
            </div>
            <div>
              <label className="block font-semibold mb-1 text-slate-300">Bağlı Liman (Lokasyon) *</label>
              <input
                required
                type="text"
                value={formData.portLocation || ''}
                onChange={(e) => setFormData({ ...formData, portLocation: e.target.value })}
                placeholder="Bodrum, Göcek, Fethiye..."
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 outline-none"
              />
            </div>
          </div>

          {/* Row 3: Specs (Capacity, Cabins, Length, Year) */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <label className="block font-semibold mb-1 text-slate-300">Kapasite (Kişi)</label>
              <input
                type="number"
                value={formData.capacity || ''}
                onChange={(e) => setFormData({ ...formData, capacity: Number(e.target.value) })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 outline-none"
              />
            </div>
            <div>
              <label className="block font-semibold mb-1 text-slate-300">Kabin Sayısı</label>
              <input
                type="number"
                value={formData.cabins || ''}
                onChange={(e) => setFormData({ ...formData, cabins: Number(e.target.value) })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 outline-none"
              />
            </div>
            <div>
              <label className="block font-semibold mb-1 text-slate-300">Uzunluk (Metre)</label>
              <input
                type="number"
                value={formData.lengthMeters || ''}
                onChange={(e) => setFormData({ ...formData, lengthMeters: Number(e.target.value) })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 outline-none"
              />
            </div>
            <div>
              <label className="block font-semibold mb-1 text-slate-300">Yapım / Yenilenme Yılı</label>
              <input
                type="number"
                value={formData.yearBuilt || ''}
                onChange={(e) => setFormData({ ...formData, yearBuilt: Number(e.target.value) })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 outline-none"
              />
            </div>
          </div>

          {/* Row 4: Cover Image & Stock Selector */}
          <div className="space-y-2">
            <label className="block font-semibold text-slate-300">Kapak Fotoğrafı URL</label>
            <input
              type="text"
              value={formData.coverImage || ''}
              onChange={(e) => setFormData({ ...formData, coverImage: e.target.value })}
              placeholder="https://images.unsplash.com/..."
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 outline-none"
            />
            <div className="text-[11px] text-slate-400">Hazır Yat Fotoğraflarından Seçin:</div>
            <div className="flex gap-2 overflow-x-auto py-1">
              {yachtStockImages.map((img, i) => (
                <div
                  key={i}
                  onClick={() => setFormData({ ...formData, coverImage: img.url })}
                  className={`w-20 h-14 rounded-lg overflow-hidden shrink-0 border-2 cursor-pointer transition ${
                    formData.coverImage === img.url ? 'border-cyan-400 ring-2 ring-cyan-500/50' : 'border-slate-700 hover:border-slate-500'
                  }`}
                >
                  <img src={img.url} alt={img.name} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>

          {/* Features list */}
          <div className="space-y-2">
            <label className="block font-semibold text-slate-300">Özellikler & Donanım</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {(formData.features || []).map((feat, idx) => (
                <span
                  key={idx}
                  className="px-2.5 py-1 rounded-lg bg-cyan-950/60 border border-cyan-800/60 text-cyan-300 text-xs flex items-center gap-1.5"
                >
                  {feat}
                  <button
                    type="button"
                    onClick={() => handleFeatureRemove(idx)}
                    className="hover:text-rose-400 ml-1 font-bold"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Örn: Jakuzi, Su Kayağı, Flybridge..."
                value={newFeature}
                onChange={(e) => setNewFeature(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleFeatureAdd();
                  }
                }}
                className="flex-1 px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-white outline-none"
              />
              <button
                type="button"
                onClick={handleFeatureAdd}
                className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-white font-semibold"
              >
                Ekle
              </button>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block font-semibold mb-1 text-slate-300">Detaylı Açıklama</label>
            <textarea
              rows={3}
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 outline-none"
            />
          </div>

          {/* Footer Submit */}
          <div className="pt-3 border-t border-slate-800 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-semibold"
            >
              İptal
            </button>
            <button
              type="submit"
              className="px-5 py-2 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-bold shadow-lg shadow-cyan-500/20 flex items-center gap-2"
            >
              <Check className="w-4 h-4" />
              Yatı Kaydet
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
